import { MapPin, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import MandalaDivider from "@/components/MandalaDivider";

const mapsUrl = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

function DirectionLink({ label, query, testid }) {
  return (
    <a
      href={mapsUrl(query)}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7B1818]/30 text-[#7B1818] text-[13px] font-semibold hover:bg-[#7B1818] hover:text-[#F4EBDB] transition-colors"
    >
      <MapPin size={14} /> {label} <ExternalLink size={12} />
    </a>
  );
}

function PlaceCard({ title, eyebrow, children, links, testid }) {
  return (
    <article data-testid={testid} className="card-soft reveal p-8 lg:p-10">
      <div className="eyebrow">{eyebrow}</div>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl text-[#7B1818] leading-tight">{title}</h2>
      <div className="mt-5"><MandalaDivider className="w-36" /></div>
      <div className="mt-6 space-y-4 text-[15px] text-[#3a2f24] leading-[1.85] font-serif-display">
        {children}
      </div>
      {links && links.length > 0 && (
        <div className="mt-7 flex flex-wrap gap-3">{links}</div>
      )}
    </article>
  );
}

export default function SacredPlaces() {
  const { t } = useLang();
  const p = t.places;
  return (
    <div data-testid="sacred-places-page">
      <PageHeader eyebrow={p.eyebrow} title={p.title} sub={p.sub} />

      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

          <PlaceCard
            testid="place-pancha-linga"
            eyebrow={p.p1Eyebrow}
            title={p.p1Title}
            links={[
              <DirectionLink key="b" label="Bhimeshwara" query="Bhimeshwara Temple Bhimanakatte Thirthahalli" testid="direction-bhimeshwara" />,
              <DirectionLink key="g" label="Gangadhareshwara" query="Gangadhareshwara Temple Bhimanakatte Thirthahalli" testid="direction-gangadhareshwara" />,
            ]}
          >
            <p>{p.p1Intro}</p>
            <ul className="list-none space-y-3 pl-0">
              <li><strong className="text-[#7B1818]">Bhimeshwara:</strong> {p.p1L1}</li>
              <li><strong className="text-[#7B1818]">Gangadhareshwara:</strong> {p.p1L2}</li>
              <li><strong className="text-[#7B1818]">Umamaheshwara:</strong> {p.p1L3}</li>
              <li><strong className="text-[#7B1818]">Bhavani Shankara &amp; Ganga Visveshwara:</strong> {p.p1L4}</li>
            </ul>
          </PlaceCard>

          <PlaceCard
            testid="place-durvasapura"
            eyebrow={p.p2Eyebrow}
            title={p.p2Title}
            links={[
              <DirectionLink key="d" label={p.nearMatha} query="Durvasapura Bhimanakatte Thirthahalli" testid="direction-durvasapura" />,
            ]}
          >
            <p>{p.p2P1}</p>
            <p>{p.p2P2}</p>
          </PlaceCard>

          <PlaceCard
            testid="place-ambutirtha"
            eyebrow={p.p3Eyebrow}
            title={p.p3Title}
            links={[
              <DirectionLink key="a" label="Ambutirtha" query="Ambutirtha Thirthahalli" testid="direction-ambutirtha" />,
              <DirectionLink key="r" label="Rama Kunda" query="Rama Kunda Thirthahalli" testid="direction-rama-kunda" />,
            ]}
          >
            <p>{p.p3P1}</p>
            <p>{p.p3P2}</p>
          </PlaceCard>

          <PlaceCard
            testid="place-sangama"
            eyebrow={p.p4Eyebrow}
            title={p.p4Title}
            links={[
              <DirectionLink key="s" label={p.viewMap} query="Bhimanakatte Thirthahalli Karnataka" testid="direction-sangama" />,
            ]}
          >
            <p>{p.p4P1}</p>
            <p>{p.p4P2}</p>
          </PlaceCard>

          <PlaceCard testid="place-agastya" eyebrow={p.p5Eyebrow} title={p.p5Title}>
            <p>{p.p5P1}</p>
            <p>{p.p5P2}</p>
          </PlaceCard>

        </div>
      </section>
    </div>
  );
}
